/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect, useState } from 'react';
import {
  Fab,
  Box,
  CircularProgress,
  Typography,
  TextField,
  Button
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import GroupWorkOutlinedIcon from '@mui/icons-material/GroupWorkOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import BusinessIcon from '@mui/icons-material/Business';
import Page from 'components/templates/Page';
import { Form, Formik } from 'formik';
import useAppDispatch from 'hooks/useAppDispatch';
import * as Yup from 'yup';
import { getCompanyAction, getUserAction } from 'store/modules/auth/actions';
import { CompanyService } from 'services/company.service';

import { CompanyType } from 'types/company';
import { getCookieSessionToken } from 'utils/session';
import { redirectToCompany } from 'utils/redirect';
import { useStyles } from './styles';

function Slogan() {
  const styles = useStyles();

  const [loading, setLoading] = useState(false);
  const token = getCookieSessionToken();
  const [userCompany, setUserCompany] = useState<CompanyType>();
  const dispatch = useAppDispatch();
  const companyId = userCompany?.id;

  const imageCover = `${process.env.REACT_APP_MEDIA_URL}/companies/${companyId}/cover.png`;
  const imageAvatar = `${process.env.REACT_APP_MEDIA_URL}/companies/${companyId}/avatar-small.png`;

  const schema = Yup.object().shape({
    about: Yup.string().max(1500)
  });

  const redirectUser = () => {
    setLoading(true);
    redirectToCompany();
    setLoading(false);
  };
  const title = (
    <span>
      Configure a
      <br />
      página de carreiras
    </span>
  );

  const saveGeneralInformation = async (values: any) => {
    setLoading(true);
    try {
      await CompanyService.updateCompany(companyId ?? '', {
        ...userCompany,
        sector: values?.sector,
        abstract: values?.about
      });
      if (token) {
        dispatch(getUserAction(token));
        dispatch(getCompanyAction(token));
        redirectUser();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getInfo = async () => {
    setLoading(true);
    try {
      const { data } = await CompanyService.getAssociatedCompany();
      setUserCompany(data[0]?.company);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInfo();
  }, []);

  return (
    <Page title={title} subtitle="">
      <Formik
        initialValues={{
          sector: '',
          about: ''
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          saveGeneralInformation(values);
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          touched,
          values
        }) => (
          <Form className={styles.container} onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="column" gap="20px">
              <Box className={styles.containerBox}>
                {!userCompany?.hasCover ? (
                  <Box className={styles.grayDiv} />
                ) : (
                  <img src={imageCover} alt="img" className={styles.grayDiv} />
                )}
                {!userCompany?.hasAvatar ? (
                  <Box className={styles.imageIcon}>
                    <BusinessIcon sx={{ color: 'rgba(0, 0, 0, 0.6)' }} />
                  </Box>
                ) : (
                  <img src={imageAvatar} alt="img" className={styles.image} />
                )}
                <Typography className={styles.company}>
                  {' '}
                  {userCompany?.fantasyName}
                </Typography>
                <Typography className={styles.slogan}>
                  {values.sector?.length > 0
                    ? values.sector
                    : 'Atividade principal da empresa'}
                </Typography>
                <Box className={styles.informations}>
                  <SellOutlinedIcon
                    className={styles.icon}
                    sx={{ width: '20px' }}
                  />{' '}
                  <Typography className={styles.valuesInformations}>
                    {userCompany?.industry?.name}
                  </Typography>
                </Box>
                <Box className={styles.informations}>
                  <PlaceOutlinedIcon
                    className={styles.icon}
                    sx={{ width: '20px' }}
                  />{' '}
                  <Typography className={styles.valuesInformations}>
                    {userCompany?.city}, {userCompany?.state}
                  </Typography>
                </Box>
                <Box
                  className={styles.informations}
                  style={{ marginBottom: '12px' }}
                >
                  <GroupWorkOutlinedIcon
                    className={styles.icon}
                    sx={{ width: '20px' }}
                  />{' '}
                  <Typography className={styles.valuesInformations}>
                    {userCompany?.amountIntervalWorkers}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <TextField
                  label="Atividade principal"
                  fullWidth
                  value={values.sector}
                  error={touched.sector && !!errors.sector}
                  helperText={touched.sector && errors.sector}
                  name="sector"
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <Typography className={styles.textExample}>
                  Ex. Consultoria, Comércio Varejista, Tecnologia
                </Typography>
              </Box>
              <Box>
                <TextField
                  placeholder="Conte-nos um pouco sobre a história da empresa, aspectos da cultura e diferenciais."
                  multiline
                  fullWidth
                  rows={3}
                  value={values.about}
                  error={touched.about && !!errors.about}
                  helperText={touched.about && errors.about}
                  name="about"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  inputProps={{ maxLength: 1500 }}
                />
                <Typography className={styles.count}>
                  {values?.about?.length}/1.500
                </Typography>
              </Box>
            </Box>
            <Box className={styles.stepsContainer}>
              <Button variant="text" onClick={redirectUser} disabled={loading}>
                Pular
              </Button>
              <Fab
                type="submit"
                disabled={loading}
                style={{ width: '56px', height: '56px' }}
                onClick={() => {
                  saveGeneralInformation(values);
                }}
              >
                {loading ? (
                  <CircularProgress style={{ color: '#fff' }} size={24} />
                ) : (
                  <ArrowForward />
                )}
              </Fab>
            </Box>
          </Form>
        )}
      </Formik>
    </Page>
  );
}

export default Slogan;
